import "./CancelOrderModal.scss";

import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Spacer } from "@amzn/stencil-react-components/layout";
import { ModalContent } from "@amzn/stencil-react-components/modal";
import { Select } from "@amzn/stencil-react-components/select";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Label, Text } from "@amzn/stencil-react-components/text";
import { IconAlertCircleFill } from "@amzn/stencil-react-icons";
import React, { FC, useCallback, useRef, useState } from "react";

import { getString } from "../../uistringlabels/uiStringUtils";
import { CANCELLATION_REASON_LABELS, CancellationReason, CancellationState, OrderDetails } from "./types";

const MODAL_TITLE = getString("hardwareOrderCancellation.modal.title");
const MODAL_SUBTITLE = getString("hardwareOrderCancellation.modal.subtitle");
const WARNING_TEXT = getString("hardwareOrderCancellation.modal.warning");
const REASON_LABEL = getString("hardwareOrderCancellation.modal.reasonLabel");
const REASON_PLACEHOLDER = getString("hardwareOrderCancellation.modal.reasonPlaceholder");
const CONFIRM_BUTTON_LABEL = getString("hardwareOrderCancellation.modal.confirmButton");
const CONFIRM_BUTTON_LOADING_LABEL = getString("hardwareOrderCancellation.modal.cancellingButton");
const KEEP_BUTTON_LABEL = getString("hardwareOrderCancellation.modal.keepButton");
const ERROR_MESSAGE = getString("hardwareOrderCancellation.error.message");
const ARIA_SPINNER_LABEL = getString("hardwareOrderCancellation.accessibility.spinnerLabel");

export interface CancelOrderModalProps {
  onClose: () => void;
  onConfirm: (reason: CancellationReason) => void;
  orderDetails: OrderDetails;
  cancellationState: CancellationState;
}

/**
 * Confirmation modal content for hardware order cancellation.
 *
 * Features:
 * - Displays asset details (name, order ID, request date)
 * - Optional cancellation reason dropdown
 * - Loading state with spinner during cancellation
 * - Inline error message on failure
 * - Focus management (default focus on "Keep order" button)
 *
 * @param props - Component props
 * @returns Cancel order modal content component
 */
export const CancelOrderModal: FC<CancelOrderModalProps> = ({
  onClose,
  onConfirm,
  orderDetails,
  cancellationState,
}) => {
  const [selectedReason, setSelectedReason] = useState<CancellationReason | undefined>(undefined);
  const keepButtonRef = useRef<HTMLButtonElement>(null);

  const { isLoading, error } = cancellationState;

  const reasonOptions = Object.values(CancellationReason).map((reason) => CANCELLATION_REASON_LABELS[reason]);

  const handleReasonChange = useCallback((selectedValue: string) => {
    const reason = Object.entries(CANCELLATION_REASON_LABELS).find(([, label]) => label === selectedValue)?.[0] as
      | CancellationReason
      | undefined;
    setSelectedReason(reason);
  }, []);

  const handleConfirm = useCallback(() => {
    if (selectedReason) {
      onConfirm(selectedReason);
    }
  }, [onConfirm, selectedReason]);

  return (
    <ModalContent
      titleText={MODAL_TITLE}
      maxWidth="500px"
      buttons={[
        <Button
          key="keep-order"
          ref={keepButtonRef}
          variant={ButtonVariant.Secondary}
          onClick={onClose}
          disabled={isLoading}
          className="cancel-order-modal__button-neutral"
          data-testid="keep-order-button"
        >
          {KEEP_BUTTON_LABEL}
        </Button>,
        <Button
          key="cancel-order"
          variant={ButtonVariant.Primary}
          onClick={handleConfirm}
          disabled={isLoading || !selectedReason}
          className={isLoading || !selectedReason ? undefined : "cancel-order-modal__button-destructive"}
          data-testid="confirm-cancel-button"
        >
          {isLoading ? (
            <>
              {CONFIRM_BUTTON_LOADING_LABEL}
              <Spinner
                size={SpinnerSize.Small}
                className="cancel-order-modal__spinner"
                aria-label={ARIA_SPINNER_LABEL}
              />
            </>
          ) : (
            CONFIRM_BUTTON_LABEL
          )}
        </Button>,
      ]}
    >
      <Col className="cancel-order-modal__content">
        {/* Subtitle */}
        <Text className="cancel-order-modal__subtitle" data-testid="modal-subtitle">
          {MODAL_SUBTITLE}
        </Text>

        {/* Asset Details Block */}
        <div className="cancel-order-modal__asset-details" data-testid="asset-details">
          <div className="cancel-order-modal__detail-row">
            <span className="cancel-order-modal__detail-label">Asset name:</span>
            <span className="cancel-order-modal__detail-value" data-testid="asset-name">
              {orderDetails.assetName}
            </span>
          </div>
          <div className="cancel-order-modal__detail-row">
            <span className="cancel-order-modal__detail-label">Order number:</span>
            <span className="cancel-order-modal__detail-value" data-testid="order-id">
              {orderDetails.orderReference}
            </span>
          </div>
          <div className="cancel-order-modal__detail-row">
            <span className="cancel-order-modal__detail-label">Request date:</span>
            <span className="cancel-order-modal__detail-value" data-testid="request-date">
              {orderDetails.requestDate}
            </span>
          </div>
        </div>

        {/* Error Message (if any) */}
        {error && (
          <div className="cancel-order-modal__error" role="alert" data-testid="error-message">
            <IconAlertCircleFill className="cancel-order-modal__error-icon" aria-hidden="true" />
            <Text className="cancel-order-modal__error-text">{ERROR_MESSAGE}</Text>
          </div>
        )}

        {/* Warning Text */}
        <div className="cancel-order-modal__warning" data-testid="warning-text">
          <Text className="cancel-order-modal__warning-text">{WARNING_TEXT}</Text>
        </div>

        {/* Reason Dropdown */}
        <div className="cancel-order-modal__reason-section">
          <Label htmlFor="cancellation-reason">{REASON_LABEL}</Label>
          <Spacer height={8} />
          <Select
            id="cancellation-reason"
            dataTestId="cancellation-reason-select"
            options={reasonOptions}
            placeholder={REASON_PLACEHOLDER}
            onChange={handleReasonChange}
            value={selectedReason ? CANCELLATION_REASON_LABELS[selectedReason] : ""}
            disabled={isLoading}
            listPositioningStrategy="fixed"
          />
        </div>
      </Col>
    </ModalContent>
  );
};

export default CancelOrderModal;
